// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { addLineCollection } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { CartLineCollectionModel, CartLineModel, InvoiceModel } from "@insite/client-framework/Types/ApiModels";

type ReorderParameter = {
    invoice: InvoiceModel;
} & HasOnSuccess<CartLineCollectionModel> &
    HasOnError<string>;

export interface ReorderResult {
    cartLines?: CartLineCollectionModel;
}

type HandlerType = ApiHandlerDiscreteParameter<ReorderParameter, CartLineCollectionModel, ReorderResult | undefined>;

export const DispatchBeginReorder: HandlerType = props => {
    props.dispatch({
        type: "Context/BeginUpdatingCart",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    if (!props.parameter?.invoice?.invoiceLines) {
        return false;
    }
    props.apiParameter = {
        cartLines: props.parameter?.invoice?.invoiceLines
            .filter(line => line.productUri)
            .map(
                (line: any) =>
                    ({
                        productId: line.productId,
                        qtyOrdered: line.qtyInvoiced <= 0 ? 1 : line.qtyInvoiced,
                        unitOfMeasure: line.unitOfMeasure,
                    }) as CartLineModel,
            ),
    } as CartLineCollectionModel;
};

export const SendDataToApi: HandlerType = async props => {
    if (!props.apiParameter?.cartLines?.length) {
        props.apiResult = undefined;
        props.dispatch({
            type: "Context/CompleteUpdatingCart",
        });
        props.parameter.onError?.(siteMessage("Custom_InvoiceHistory_NoProductsForReorder") as string);
        return false;
    }
    const cartLines = await addLineCollection({
        cartId: API_URL_CURRENT_FRAGMENT,
        cartLineCollection: props.apiParameter,
    });

    props.apiResult = {
        cartLines,
    };
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.apiResult) {
        return;
    }
    props.parameter.onSuccess?.(props.apiResult?.cartLines as CartLineCollectionModel);
};

export const DispatchCompleteReorder: HandlerType = props => {
    props.dispatch({
        type: "Context/CompleteUpdatingCart",
    });
};

export const LoadCart: HandlerType = props => {
    props.dispatch(loadCurrentCart());
};

export const chain = [
    DispatchBeginReorder,
    PopulateApiParameter,
    SendDataToApi,
    ExecuteOnSuccessCallback,
    LoadCart,
    DispatchCompleteReorder,
];

const reorderInvoice = createHandlerChainRunner(chain, "ReorderInvoice");
export default reorderInvoice;
